import { useId, useRef, useState } from "react";
import { useFileLibrary, type LibraryItem } from "./fileLibrary.js";
import { formatAttachmentSize } from "./lessonNoteAttachmentApi.js";

export function LibraryDocumentPicker({ disabled, onSelect, scope = "resources" }: {
  disabled: boolean; onSelect: (item: LibraryItem) => Promise<boolean>; scope?: string;
}) {
  const id = useId();
  const selecting = useRef(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const result = useFileLibrary(query, "all", 0, true, 0, open && query.trim().length > 0 && !disabled, scope);
  const items = result.data?.items ?? [];
  async function choose(item: LibraryItem) {
    if (disabled || selecting.current) return;
    selecting.current = true;
    try {
      if (await onSelect(item)) { setQuery(""); setOpen(false); setActive(-1); }
    } finally { selecting.current = false; }
  }
  const expanded = open && query.trim().length > 0 && !disabled;
  return <div className="library-picker" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}>
    <label htmlFor={id}>Find an existing Hub document</label>
    <input id={id} type="search" role="combobox" autoComplete="off" placeholder="Search names and descriptions…"
      maxLength={200} value={query} disabled={disabled} aria-autocomplete="list" aria-expanded={expanded}
      aria-controls={`${id}-results`} aria-activedescendant={expanded && items[active] ? `${id}-option-${active}` : undefined}
      onFocus={() => setOpen(true)}
      onChange={event => { setQuery(event.target.value); setOpen(true); setActive(-1); }}
      onKeyDown={event => {
        if (event.key === "Escape") { setOpen(false); setActive(-1); }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault(); setOpen(true);
          setActive(current => items.length ? (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length : -1);
        }
        if (event.key === "Enter") { event.preventDefault(); if (expanded && items[active]) void choose(items[active]); }
      }} />
    {expanded && <div className="library-suggestions">
      {result.loading && <p role="status">Searching documents…</p>}
      {result.error && <p role="alert" className="error">{result.error}</p>}
      {result.data && !items.length && <p role="status">No documents match. Try another name or fewer words.</p>}
      <ul id={`${id}-results`} role="listbox" aria-label="Matching Hub documents">
        {items.map((item, index) => <li key={`${item.source}:${item.id}`} id={`${id}-option-${index}`}
          role="option" aria-selected={active === index} className={active === index ? "active" : ""}
          onMouseDown={event => event.preventDefault()} onClick={() => void choose(item)}>
          <strong>{item.title}</strong>
          <span className="small muted">{item.context} · {item.kind.toUpperCase()}{formatAttachmentSize(item.fileSize) ? ` · ${formatAttachmentSize(item.fileSize)}` : ""}</span>
        </li>)}
      </ul>
      {result.data?.nextOffset !== null && result.data?.nextOffset !== undefined && <p className="small muted">Keep typing to narrow the matches.</p>}
    </div>}
  </div>;
}
